import { useField } from "formik";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import {
  GenericField,
  InputProps,
} from "@/lib/Components/Form/Fields/GenericField";
import {
  ALL_PLATFORMS,
  PlatformIcon,
} from "@/app/Promotions/Components/PlatformIcon";
import { promotionPlatformLabelMap } from "@/app/Promotions/Utils/promotionMaps";
import { Badge } from "@/components/catalyst/badge";
import { PromotionPlatform } from "@/gql/graphql";
import { cn } from "@/lib/utils";

type PlatformSelectInputProps = InputProps & {
  disabledPlatforms?: PromotionPlatform[];
  disabledLabel?: string;
};

export function PlatformSelectInput({
  disabledPlatforms = [],
  disabledLabel,
  ...props
}: PlatformSelectInputProps) {
  const [field, , helpers] = useField<PromotionPlatform[]>(props.name);
  const selected = field.value ?? [];

  return (
    <GenericField
      {...props}
      viewNode={
        <span>
          {selected.map((p) => promotionPlatformLabelMap[p]).join(", ") || "--"}
        </span>
      }
    >
      <div className="mt-1 grid grid-cols-2 gap-3 lg:grid-cols-1">
        {ALL_PLATFORMS.map((platform) => {
          const isDisabled = disabledPlatforms.includes(platform);
          const isSelected = !isDisabled && selected.includes(platform);

          return (
            <button
              key={platform}
              type="button"
              disabled={isDisabled}
              aria-pressed={isSelected}
              onClick={() =>
                helpers.setValue(
                  isSelected
                    ? selected.filter((p) => p !== platform)
                    : [...selected, platform],
                )
              }
              className={cn(
                "flex items-center gap-3 rounded-lg border p-3 text-left transition",
                isDisabled
                  ? "cursor-not-allowed border-gray-200 bg-gray-50"
                  : isSelected
                    ? "border-blue-600 bg-blue-50 ring-1 ring-blue-600"
                    : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50",
              )}
            >
              <PlatformIcon
                platform={platform}
                enabled={isSelected}
                className="h-6 w-6"
              />

              <span
                className={cn(
                  "truncate text-sm font-medium",
                  isSelected ? "text-gray-900" : "text-gray-500",
                )}
              >
                {promotionPlatformLabelMap[platform]}
              </span>

              {isDisabled ? (
                <Badge color="zinc" className="ml-auto shrink-0">
                  {disabledLabel}
                </Badge>
              ) : isSelected ? (
                <CheckCircleIcon className="ml-auto size-5 shrink-0 text-blue-600" />
              ) : (
                <span className="ml-auto size-5 shrink-0 rounded-full border-2 border-gray-300" />
              )}
            </button>
          );
        })}
      </div>
    </GenericField>
  );
}
